// Safe mode: a client-side guard on every call that changes mods in the game.
//
// It exists so the UI can be exercised — clicked through, screenshotted, handed
// to someone else to try — against a real logged-in account with no way for a
// stray click to sell, slice, level or calibrate anything. The guard sits at
// BaseAPI.postToApi, the one place every write goes through, so it cannot be
// bypassed by a caller that forgets about it.
//
// Two flavours:
//
//   block     — the request is never sent. Guaranteed safe, but the UI stops at
//               the error dialog, so anything downstream of a successful call
//               (the results modal, the refreshed roster) can't be reached.
//   simulate  — the request is sent with the API's own `simulation` flag set
//               true. Every mod endpoint already carries `simulation: false` in
//               its payload, so the server side of this exists; what it returns
//               is the server's business. UNVERIFIED — until someone has checked
//               that a simulated call really leaves the account untouched, use
//               block. A body with no `simulation` field falls back to block
//               rather than guessing.
//
// Turn it on either way:
//   REACT_APP_SAFE_MODE=block npm start        (whole session, can't be clicked off)
//   localStorage.setItem("hotbot.safeMode", "block")    (this browser, until cleared)

export type SafeMode = "off" | "block" | "simulate";

const STORAGE_KEY = "hotbot.safeMode";

// Endpoints that change something in the player's game account. Matched as
// prefixes against the endpoint string passed to postToApi. Reads (score
// history, definition fetches, filters) are deliberately absent: blocking them
// would make the pages untestable, and they alter nothing.
const MUTATING_ENDPOINTS = [
    "mods/sell",
    "mods/level",
    "mods/tier",          // slice
    "mods/lock",
    "mods/unlock",
    "mods/equip",
    "mods/unequip",
    "mods/reroll",        // calibrate
    "mods/acceptreroll",
    "mods/reveal",
    "mods/batch",
    "mods/task/",         // the long-running variants of all of the above
];

function parseMode(raw: string | null | undefined): SafeMode
{
    // "1" is accepted for the obvious `REACT_APP_SAFE_MODE=1`, and means the
    // guaranteed flavour rather than the unverified one.
    if (raw === "1" || raw === "block")
        return "block";
    if (raw === "simulate")
        return "simulate";
    return "off";
}

export function getSafeMode(): SafeMode
{
    const forced = parseMode(process.env.REACT_APP_SAFE_MODE);
    if (forced !== "off")
        return forced;
    try
    {
        return parseMode(window.localStorage.getItem(STORAGE_KEY));
    }
    catch
    {
        // Private browsing or a blocked storage partition: fall back to off, since
        // the env var is the reliable switch and this one is a convenience.
        return "off";
    }
}

export function isSafeMode(): boolean
{
    return getSafeMode() !== "off";
}

// Only meaningful for the localStorage switch; the env var outranks it and can
// only be changed by restarting the dev server.
export function setSafeMode(mode: SafeMode): void
{
    try
    {
        if (mode === "off")
            window.localStorage.removeItem(STORAGE_KEY);
        else
            window.localStorage.setItem(STORAGE_KEY, mode);
    }
    catch { /* nothing to do — getSafeMode falls back to the env var */ }
}

export function safeModeIsForced(): boolean
{
    return parseMode(process.env.REACT_APP_SAFE_MODE) !== "off";
}

export function isMutatingEndpoint(endpoint: string): boolean
{
    const path = endpoint.replace(/^\/+/, "").toLowerCase();
    return MUTATING_ENDPOINTS.some(m => path.startsWith(m));
}

export interface SafeModeDecision
{
    action: "send" | "simulate" | "block";
    body?: any;
}

// What to do with one outgoing request. Returns the body to send when the call
// is allowed through in simulated form, so the caller never has to know how the
// flag is spelled.
export function safeModeDecision(endpoint: string, body: any): SafeModeDecision
{
    const mode = getSafeMode();
    if (mode === "off" || !isMutatingEndpoint(endpoint))
        return { action: "send" };
    // Only endpoints that already declare `simulation` can be simulated; anything
    // else would be a guess, and a wrong guess here sells someone's mods.
    if (mode === "simulate" && body && typeof body === "object" && "simulation" in body)
        return { action: "simulate", body: { ...body, simulation: true } };
    return { action: "block" };
}

// Thrown shape matches the API's own errors ({ errorMessage }), so every existing
// catch block reports this the same way it reports a server refusal.
export function safeModeBlock(endpoint: string): { errorMessage: string; safeMode: true }
{
    return {
        errorMessage: `Safe mode is on, so "${endpoint}" was not sent — nothing in your account changed. `
            + "Turn it off in the banner at the top of the page to make real changes.",
        safeMode: true,
    };
}
